<!--
  The wrapper is a propagation SHIELD, not a control: it stops the
  document-level click that closes the panel from firing on clicks inside it.
  Giving it keyboard handlers and a tabindex to satisfy the rule would put a
  do-nothing stop in the tab order, which is worse for keyboard users than the
  rule it satisfies. The real controls inside are all <button>.
-->
<!-- eslint-disable-next-line @angular-eslint/template/click-events-have-key-events, @angular-eslint/template/interactive-supports-focus -->
<div
  class="relative flex items-center"
  #panelRoot
  (click)="$event.stopPropagation()"
  >
  <button
    #triggerButton
    type="button"
    class="relative z-[80] flex items-center justify-center h-5 w-5 cursor-pointer"
    matTooltip="Notifications"
    matTooltipClass="cus-primary-tooltip"
    aria-haspopup="dialog"
    aria-controls="notification-center-panel"
    [attr.aria-expanded]="isPanelOpen"
    (click)="togglePanel($event)"
    >
    <mat-icon class="notification-bell-icon" svgIcon="c:bell"></mat-icon>
    @if (unreadCount$ | async; as unreadCount) {
      @if (unreadCount > 0) {
        <!-- Presence, not arithmetic: a count pinned at 99+ is wallpaper. The
             exact figure lives in the panel header, and is still announced. -->
        <span
          class="notification-dot"
          [attr.aria-label]="unreadCount + ' unread notifications'"
          [matTooltip]="unreadCount + ' unread'"
        ></span>
      }
    }
  </button>

  @if (isPanelOpen) {
    <div
      #panelContainer
      id="notification-center-panel"
      class="notification-panel notification-panel--glass fixed w-full max-w-[380px] z-[70] flex flex-col"
      role="dialog"
      aria-labelledby="notification-center-title"
      tabindex="-1"
      (keydown)="focusTrap($event)"
      >
      <!--
        Two rows, because 380px cannot hold a title, a three-way filter, a text
        action and a close button on one line without them colliding. Row one is
        identity and dismissal; row two is everything that changes the list.
      -->
      <header class="notification-panel__header">
        <div class="notification-panel__heading">
          <p id="notification-center-title" class="notification-panel__title">Notifications</p>
          <span class="notification-panel__heading-actions">
            @if (unreadCount$ | async; as unreadCount) {
              @if (unreadCount > 0) {
                <button
                  type="button"
                  class="notification-markall"
                  (click)="onMarkAllRead($event)"
                  [disabled]="markingAllRead"
                  >
                  {{ markingAllRead ? 'Marking…' : 'Mark all read' }}
                </button>
              }
            }
            <button
              type="button"
              #closeButton
              class="notification-close-button"
              (click)="closePanel($event)"
              aria-label="Close notifications panel"
              >
              <mat-icon svgIcon="c:x"></mat-icon>
            </button>
          </span>
        </div>
        <div class="notification-panel__actions">
          @if (filter$ | async; as activeFilter) {
            <!--
              A segmented control, not three separate pills. The three options are
              mutually exclusive, so they share one track and the selection rides
              inside it — the shape itself says "pick one of these".
            -->
            <div class="notification-segmented" role="group" aria-label="Filter notifications">
              <button
                type="button"
                #filterAllButton
                class="notification-segmented__option"
                [class.notification-segmented__option--active]="activeFilter === 'all'"
                [attr.aria-pressed]="activeFilter === 'all'"
                (click)="onFilterChange($event, 'all')"
                >
                All
              </button>
              <button
                type="button"
                class="notification-segmented__option"
                [class.notification-segmented__option--active]="activeFilter === 'unread'"
                [attr.aria-pressed]="activeFilter === 'unread'"
                (click)="onFilterChange($event, 'unread')"
                >
                Unread
                @if (unreadCount$ | async; as unreadCount) {
                  @if (unreadCount > 0) {
                    <span class="notification-segmented__count">{{ unreadCount }}</span>
                  }
                }
              </button>
              <button
                type="button"
                class="notification-segmented__option"
                [class.notification-segmented__option--active]="activeFilter === 'read'"
                [attr.aria-pressed]="activeFilter === 'read'"
                (click)="onFilterChange($event, 'read')"
                >
                Read
              </button>
            </div>
          }
        </div>
      </header>
      <section class="notification-panel__content">
        @if (initialisationMessage; as initMessage) {
          <div class="px-4 py-6 text-xs text-gray-600">
            {{ initMessage }}
          </div>
        }
        @if (!initialisationMessage) {
          @if ({
            notifications: notifications$ | async,
            selected: selectedNotification$ | async,
            loading: loading$ | async,
            error: error$ | async
            }; as vm) {
            <div>
              <app-notification-tile-list
                [notifications]="vm.notifications ?? []"
                [expandedId]="vm.selected?.id ?? null"
                [loading]="vm.loading ?? false"
                [error]="vm.error"
                (notificationSelected)="onNotificationClick($event)"
                (groupCleared)="onClearGroup($event)"
                (markedRead)="onMarkRead($event)"
                (dismissed)="onDismiss($event)"
              ></app-notification-tile-list>
            </div>
          }
        }
      </section>

      <!--
        Only rendered when the API reports a retention window. With retention
        off there is no purge job running, and a footer promising one would be
        a lie the UI told on the backend's behalf.
      -->
      @if (retentionDays$ | async; as retentionDays) {
        <p class="notification-panel__retention">
          @if (retentionDays === 1) {
            Older cleared automatically after 1 day
          } @else {
            Older cleared automatically after {{ retentionDays }} days
          }
        </p>
      }
    </div>
  }
</div>
